import { Button } from '@kluein/klue-ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppUIContext } from './appUIContext';
import {
  RECOVERY_REQUESTS_URL,
  usePendingRecoveryRequests,
} from './usePendingRecoveryRequests';

/**
 * Component that shows toast notification for pending recovery requests (admins only).
 *
 * Must be rendered inside AppUIProvider to have access to the toast context. Render it once per
 * page: App.tsx mounts it for Current Compete, and Compete 2.0 has its own RecoveryRequestsNotifier.
 */
export function RecoveryRequestsNotifier(): null {
  const { t } = useTranslation(['Session']);
  const { toast } = useAppUIContext();
  const toastIdRef = useRef<string | null>(null);
  const pendingRecoveryCount = usePendingRecoveryRequests();

  useEffect(() => {
    // Defer to next tick to avoid updating parent state during render
    const timeoutId = setTimeout(() => {
      if (pendingRecoveryCount > 0 && !toastIdRef.current) {
        toastIdRef.current = toast.open({
          title: t('Session:recoveryRequests.title'),
          message: t('Session:recoveryRequests.message', {
            count: pendingRecoveryCount,
          }),
          action: (
            <Button
              alignSelf="end"
              variant="flat-outlined"
              onClick={() => {
                if (toastIdRef.current) {
                  toast.close(toastIdRef.current, true);
                  toastIdRef.current = null;
                }
                window.location.href = RECOVERY_REQUESTS_URL;
              }}
              label={t('Session:recoveryRequests.link')}
            />
          ),
        });
      } else if (pendingRecoveryCount === 0 && toastIdRef.current) {
        // Close toast and reset when no more pending requests
        toast.close(toastIdRef.current, true);
        toastIdRef.current = null;
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [pendingRecoveryCount, toast, t]);

  return null;
}
